/* Cross-store checkout for storefront theme - 2026-10.
 * Uses Shopify's Storefront GraphQL API on the destination shop.
 * Never sends Admin API credentials or payment information.
 */
(() => {
  'use strict';
  const el = document.getElementById('crossstore-configuration');
  if (!el) return;
  let config;
  try { config = JSON.parse(el.textContent); } catch (_) { return; }
  if (!config.enabled) return;
  const domain = String(config.domain || '').trim().replace(/^https?:\/\//,'').replace(/\/.*$/,'');
  if (!/^[a-z0-9][a-z0-9.-]*\.myshopify\.com$/i.test(domain)) {
    console.error('[CrossStore] Invalid target myshopify.com domain');
    return;
  }
  let handleMap = {};
  try { handleMap = JSON.parse(config.handleMap || '{}'); } catch (error) { console.warn('[CrossStore] Invalid handle map JSON', error); }
  if (!handleMap || Array.isArray(handleMap) || typeof handleMap !== 'object') handleMap = {};
  const endpoint = `https://${domain}/api/2026-10/graphql.json`;
  const headers = { 'Content-Type': 'application/json' };
  if (String(config.publicToken || '').trim()) headers['X-Shopify-Storefront-Access-Token'] = config.publicToken.trim();
  let busy = false;
  const productQuery = `query CrossStoreProduct($handle: String!) {
    product(handle: $handle) {
      id handle title
      variants(first: 250) { edges { node { id title availableForSale selectedOptions { name value } } } }
    }
  }`;
  const cartMutation = `mutation CrossStoreCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id checkoutUrl }
      userErrors { field message code }
    }
  }`;
  async function graphQL(query, variables) {
    const res = await fetch(endpoint, { method: 'POST', mode: 'cors', headers, body: JSON.stringify({ query, variables }) });
    if (!res.ok) throw new Error(`Destination Storefront API error (HTTP ${res.status})`);
    const json = await res.json();
    if (json.errors && json.errors.length) throw new Error(json.errors.map(x => x.message).join('; '));
    return json.data;
  }
  function normalize(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  function optionsMatch(item, variant) {
    const source = (item.options_with_values || []).filter(o => normalize(o.name) !== 'title');
    const dest = (variant.selectedOptions || []).filter(o => normalize(o.name) !== 'title');
    // Single default variant: both have no meaningful options.
    if (!source.length && !dest.length) return true;
    if (source.length !== dest.length) return false;
    return source.every(o => dest.some(d => normalize(d.name) === normalize(o.name) && normalize(d.value) === normalize(o.value)));
  }
  async function redirectCart(button) {
    if (busy) return;
    busy = true;
    if (button) { button.dataset.crossstoreProcessing='true'; button.setAttribute('aria-busy','true'); }
    try {
      const response = await fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js', {credentials:'same-origin',cache:'no-store'});
      if (!response.ok) throw new Error('Cannot read storefront cart');
      const cart = await response.json();
      if (!cart.items || !cart.items.length) throw new Error('The cart is empty');
      const handles = [...new Set(cart.items.map(i => handleMap[i.handle] || i.handle))];
      const found = await Promise.all(handles.map(async handle => {
        const data = await graphQL(productQuery,{handle});
        if (!data || !data.product) throw new Error(`Product not published on checkout store: ${handle}`);
        return [handle,data.product];
      }));
      const products = new Map(found);
      const lines = cart.items.map(item => {
        const handle = handleMap[item.handle] || item.handle;
        const p = products.get(handle);
        const variants = p.variants.edges.map(e=>e.node);
        const matches = variants.filter(v=>optionsMatch(item,v));
        if (matches.length !== 1) throw new Error(`Could not uniquely match variant: ${item.product_title} / ${item.variant_title || 'Default'}`);
        if (!matches[0].availableForSale) throw new Error(`Unavailable on checkout store: ${item.product_title}`);
        if (item.selling_plan_allocation) throw new Error(`Subscriptions need a matching selling plan: ${item.product_title}`);
        return {
          merchandiseId:matches[0].id,
          quantity:item.quantity,
          ...(item.properties && typeof item.properties === 'object' ? {attributes:Object.entries(item.properties).filter(([k,v])=>v!=null && String(v)!=='').map(([key,value])=>({key,value:String(value)}))} : {})
        };
      });
      const input={lines};
      if (cart.note) input.note = String(cart.note);
      if (cart.attributes && typeof cart.attributes === 'object') input.attributes=Object.entries(cart.attributes).map(([key,value])=>({key,value:String(value)}));
      // Discount code may be supplied by a promotion URL; do not claim to transfer applied automatic promotions.
      const result = await graphQL(cartMutation,{input});
      const output = result && result.cartCreate;
      if (!output || (output.userErrors && output.userErrors.length)) throw new Error((output?.userErrors || []).map(e=>e.message).join('; ') || 'Cannot create checkout cart');
      const checkout = output.cart?.checkoutUrl;
      if (!checkout || !/^https:\/\//.test(checkout)) throw new Error('Checkout URL missing');
      window.location.assign(checkout);
    } catch (error) {
      console.error('[CrossStore]',error);
      alert('We could not transfer your cart to checkout. Please try again.\n\n' + error.message);
      busy=false;
      if (button) {delete button.dataset.crossstoreProcessing;button.removeAttribute('aria-busy');}
    }
  }
  const isCheckout = node => node && node.matches && (
    node.matches('button[name="checkout"], input[name="checkout"], #CartDrawer-Checkout, #checkout, [data-crossstore-checkout]')
  );
  // Capture before Shopify/theme handlers. This covers dynamically rebuilt drawer and cart pages.
  document.addEventListener('click', e => {
    const target=e.target.closest && e.target.closest('button[name="checkout"], input[name="checkout"], #CartDrawer-Checkout, #checkout, [data-crossstore-checkout]');
    if (!isCheckout(target) || target.disabled) return;
    e.preventDefault();e.stopImmediatePropagation();redirectCart(target);
  },true);
  document.addEventListener('submit', e => {
    const submitter=e.submitter;
    if (isCheckout(submitter) || (e.target && e.target.id==='cart-notification-form')) {
      e.preventDefault();e.stopImmediatePropagation();redirectCart(submitter);
    }
  },true);
  window.CrossStoreCheckout={redirectCart};
})();
